import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card } from "@/components/shadcn/card";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Camera,
  ChevronDown,
  ChevronRight,
  ZoomIn,
  AlertCircle,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Film,
  Bot,
  ChevronLeft,
  Navigation,
  MousePointer,
  Type,
  Download,
  Keyboard,
  Cookie,
  Eraser,
  Code,
  ArrowDown,
  RefreshCw,
  Globe,
  FileDown,
  FileUp,
  ArrowLeftRight,
  StopCircle,
  Terminal,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { AgentChatView, type ChatMessage } from "./AgentChatView";
import { groupSubStepsByIteration } from "./loopIterations";

const formatMs = (ms: number) =>
  ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;

const ACTION_ICONS: Record<string, LucideIcon> = {
  navigate: Globe,
  reload: RefreshCw,
  switch_tab: ArrowLeftRight,
  click: MousePointer,
  type: Type,
  fill: Type,
  press: Keyboard,
  upload: FileUp,
  extract: Download,
  screenshot: Camera,
  download: FileDown,
  extract_cookies: Cookie,
  extract_request_headers: Download,
  extract_url: Globe,
  set_cookies: Cookie,
  clear_cookies: Eraser,
  wait_for: Clock,
  wait_for_timeout: Clock,
  wait_for_load: Clock,
  scroll: ArrowDown,
  if: Code,
  loop: Code,
  break: StopCircle,
  set_variable: FileDown,
  agent: Bot,
  run_script: Terminal,
};

const ACTION_LABELS: Record<string, string> = {
  navigate: "Navigate",
  reload: "Reload",
  switch_tab: "Switch Tab",
  click: "Click",
  type: "Type",
  fill: "Fill",
  press: "Press Key",
  upload: "Upload File",
  extract: "Extract",
  screenshot: "Screenshot",
  download: "Download",
  extract_cookies: "Get Cookies",
  extract_request_headers: "Extract Headers",
  extract_url: "Extract Current URL",
  set_cookies: "Set Cookies",
  clear_cookies: "Clear Cookies",
  wait_for: "Wait For",
  wait_for_timeout: "Wait",
  wait_for_load: "Wait for Load",
  scroll: "Scroll",
  if: "If",
  loop: "Loop",
  break: "Break Loop",
  set_variable: "Log Value",
  agent: "AI Agent",
  run_script: "Run Script",
};

interface AgentSubStep {
  tool_name: string;
  /** Display name from the backend; older runs carry only `tool_name`. */
  tool_label?: string | null;
  screenshot?: string | null;
  duration_ms: number;
}

const agentToolName = (sub: AgentSubStep) => sub.tool_label ?? sub.tool_name;

interface StepResult {
  step_id: string;
  step_number?: number;
  total_steps?: number;
  action_type: string;
  status: "success" | "failed" | "pending";
  duration_ms: number;
  error?: string | null;
  screenshot?: string | null;
  extracted_data?: any;
  description?: string | null;
  agent_sub_steps?: AgentSubStep[] | null;
  agent_chat_messages?: ChatMessage[] | null;
  sub_steps?: StepResult[] | null;
  success?: boolean;
  /** 1-based loop iteration this child ran in; set by the V2 executor only. */
  iteration?: number | null;
}

export interface ExecutionResults {
  success: boolean;
  total_steps: number;
  steps: StepResult[];
  variables?: Record<string, any>;
  duration_ms: number;
  error?: string;
  note?: string;
}

interface PlaybookResultsViewerProps {
  results: ExecutionResults | string;
  className?: string;
}

function AgentSubStepSlideshow({ subSteps }: { subSteps: AgentSubStep[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const screenshotSteps = subSteps.filter((s) => s.screenshot);

  if (screenshotSteps.length === 0) return null;

  const current = screenshotSteps[currentIndex];

  const formatMs = (ms: number) =>
    ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium">Agent Actions</p>
          <Badge variant="outline" className="text-xs">
            {screenshotSteps.length} steps
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() =>
              setCurrentIndex(
                (prev) =>
                  (prev - 1 + screenshotSteps.length) % screenshotSteps.length
              )
            }
            disabled={screenshotSteps.length <= 1}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-muted-foreground px-1.5 tabular-nums">
            {currentIndex + 1} / {screenshotSteps.length}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() =>
              setCurrentIndex((prev) => (prev + 1) % screenshotSteps.length)
            }
            disabled={screenshotSteps.length <= 1}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="relative rounded-md overflow-hidden border">
        <img
          src={`data:image/png;base64,${current.screenshot}`}
          alt={`Agent action: ${agentToolName(current)}`}
          className="w-full h-auto"
        />
        <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2.5 py-1 rounded text-xs backdrop-blur-sm flex items-center gap-2">
          <span className="font-mono">{agentToolName(current)}</span>
          <span className="text-white/70">{formatMs(current.duration_ms)}</span>
        </div>
      </div>
    </div>
  );
}

function SubStepCard({
  subStep,
  index,
  subKey,
  expanded,
  onToggle,
  onScreenshotClick,
}: {
  subStep: StepResult;
  index: number;
  subKey: string;
  expanded: boolean;
  onToggle: (key: string) => void;
  onScreenshotClick: (screenshot: string) => void;
}) {
  const SubActionIcon = ACTION_ICONS[subStep.action_type] || AlertCircle;
  const succeeded = subStep.status === "success" || subStep.success === true;

  return (
    <Card className="overflow-hidden p-0 gap-0">
      <button
        className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-muted/50 transition-colors text-left"
        onClick={() => onToggle(subKey)}
      >
        <div className="flex-shrink-0">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
        <div className="flex-shrink-0">
          <div
            className={cn(
              "w-5 h-5 rounded flex items-center justify-center",
              succeeded ? "bg-muted text-foreground" : "bg-red-100 text-red-600"
            )}
          >
            <SubActionIcon className="h-3 w-3" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-[11px] font-mono text-muted-foreground">
              #{index + 1}
            </span>
            <span className="text-sm font-medium truncate">
              {ACTION_LABELS[subStep.action_type] || subStep.action_type}
            </span>
            {subStep.description && (
              <span className="text-[11px] text-muted-foreground truncate">
                {subStep.description}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {formatMs(subStep.duration_ms)}
          </div>
        </div>
        <div className="flex-shrink-0">
          {succeeded ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-success-500" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-red-500" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1.5 border-t bg-muted/10 space-y-2.5">
          {subStep.error && (
            <div className="p-2 bg-red-50 dark:bg-red-950 rounded border border-red-200 dark:border-red-800">
              <p className="text-xs text-red-700 dark:text-red-300 font-mono">
                {subStep.error}
              </p>
            </div>
          )}
          {subStep.extracted_data && (
            <pre className="text-xs font-mono p-2 bg-muted/50 rounded border overflow-auto max-h-[300px]">
              {typeof subStep.extracted_data === "string"
                ? subStep.extracted_data
                : JSON.stringify(subStep.extracted_data, null, 2)}
            </pre>
          )}
          {subStep.screenshot && (
            <div
              className="relative rounded-md overflow-hidden border cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              onClick={() => onScreenshotClick(subStep.screenshot!)}
            >
              <img
                src={`data:image/png;base64,${subStep.screenshot}`}
                alt={`Screenshot for ${subStep.step_id}`}
                className="w-full h-auto"
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function LoopIterationSteps({
  parentKey,
  subSteps,
  expandedSteps,
  onToggle,
  onScreenshotClick,
}: {
  parentKey: string;
  subSteps: StepResult[];
  expandedSteps: Set<string>;
  onToggle: (key: string) => void;
  onScreenshotClick: (screenshot: string) => void;
}) {
  const groups = useMemo(() => groupSubStepsByIteration(subSteps), [subSteps]);
  const [iterationIndex, setIterationIndex] = useState(0);
  const current = Math.max(0, Math.min(iterationIndex, groups.length - 1));
  const currentSteps = groups[current] ?? [];

  return (
    <div className="space-y-3">
      {groups.length > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">
            Iteration {current + 1} of {groups.length}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              aria-label="Previous iteration"
              onClick={() => setIterationIndex(current - 1)}
              disabled={current === 0}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              aria-label="Next iteration"
              onClick={() => setIterationIndex(current + 1)}
              disabled={current === groups.length - 1}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
      {currentSteps.map((subStep, subIdx) => {
        const subKey = `${parentKey}:${current + 1}:${subStep.step_id}:${subIdx}`;
        return (
          <SubStepCard
            key={subKey}
            subStep={subStep}
            index={subIdx}
            subKey={subKey}
            expanded={expandedSteps.has(subKey)}
            onToggle={onToggle}
            onScreenshotClick={onScreenshotClick}
          />
        );
      })}
    </div>
  );
}

export function PlaybookResultsViewer({
  results,
  className,
}: PlaybookResultsViewerProps) {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [selectedScreenshot, setSelectedScreenshot] = useState<string | null>(
    null
  );
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(500); // 2 seconds per slide
  const slideshowTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Parse results if it's a string
  const parsedResults: ExecutionResults =
    typeof results === "string" ? JSON.parse(results) : results;

  // Get all screenshots, including agent sub-step and loop sub-step screenshots
  type ScreenshotEntry = {
    screenshot: string | null | undefined;
    step_id: string;
    step_number: number;
    action_type: string;
    status: string;
  };

  const collectScreenshots = (
    steps: StepResult[],
    baseNumber?: number,
    prefix?: string
  ): ScreenshotEntry[] => {
    return steps.flatMap((step, index) => {
      const stepNumber = step.step_number || baseNumber || index + 1;
      const items: ScreenshotEntry[] = [];
      const caption = ACTION_LABELS[step.action_type] || step.action_type;
      const actionLabel = prefix
        ? `${prefix}${step.action_type}`
        : step.action_type;

      // Loop/if sub-steps
      if (step.sub_steps && step.sub_steps.length > 0) {
        const subPrefix =
          step.action_type === "loop"
            ? "loop:"
            : step.action_type === "if"
              ? "if:"
              : prefix || "";
        items.push(
          ...collectScreenshots(step.sub_steps, stepNumber, subPrefix)
        );
      }

      // Agent sub-steps
      if (step.agent_sub_steps && step.agent_sub_steps.length > 0) {
        step.agent_sub_steps.forEach((sub) => {
          if (sub.screenshot) {
            items.push({
              screenshot: sub.screenshot,
              step_id: `${caption} → ${agentToolName(sub)}`,
              step_number: stepNumber,
              action_type: `agent:${agentToolName(sub)}`,
              status: step.status,
            });
          }
        });
      } else if (
        step.screenshot &&
        !(step.sub_steps && step.sub_steps.length > 0)
      ) {
        items.push({
          screenshot: step.screenshot,
          step_id: caption,
          step_number: stepNumber,
          action_type: actionLabel,
          status: step.status,
        });
      }

      return items;
    });
  };

  const screenshots = collectScreenshots(parsedResults.steps);

  // Slideshow auto-advance effect
  useEffect(() => {
    if (showSlideshow && isPlaying && screenshots.length > 0) {
      slideshowTimerRef.current = setTimeout(() => {
        setCurrentSlideIndex((prev) => (prev + 1) % screenshots.length);
      }, playbackSpeed);
    }

    return () => {
      if (slideshowTimerRef.current) {
        clearTimeout(slideshowTimerRef.current);
      }
    };
  }, [
    showSlideshow,
    isPlaying,
    currentSlideIndex,
    playbackSpeed,
    screenshots.length,
  ]);

  const handleOpenSlideshow = () => {
    setShowSlideshow(true);
    setCurrentSlideIndex(0);
    setIsPlaying(true);
  };

  const handleCloseSlideshow = () => {
    setShowSlideshow(false);
    setIsPlaying(false);
    if (slideshowTimerRef.current) {
      clearTimeout(slideshowTimerRef.current);
    }
  };

  const handleNextSlide = () => {
    setCurrentSlideIndex((prev) => (prev + 1) % screenshots.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlideIndex(
      (prev) => (prev - 1 + screenshots.length) % screenshots.length
    );
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleStep = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className={cn("space-y-3 mb-2", className)}>
      {/* Summary Header */}
      <Card className="p-3 gap-0">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium truncate">
                Playbook Execution{" "}
                {parsedResults.success ? "Completed" : "Failed"}
              </h3>
              <Badge
                variant={parsedResults.success ? "success" : "destructive"}
              >
                {parsedResults.success ? "Success" : "Failed"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {parsedResults.total_steps} steps •{" "}
              {formatDuration(parsedResults.duration_ms)}
              {screenshots.length > 0 && (
                <> • {screenshots.length} screenshots</>
              )}
            </p>
          </div>
          {screenshots.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenSlideshow}
              className="h-7 text-xs shrink-0"
            >
              <Film className="h-3 w-3" />
              Slideshow
            </Button>
          )}
        </div>

        {parsedResults.error && (
          <div className="mt-2 p-2 bg-red-50 dark:bg-red-950 rounded-md">
            <p className="text-xs text-red-800 dark:text-red-200">
              <AlertCircle className="h-3.5 w-3.5 inline mr-1.5" />
              {parsedResults.error}
            </p>
          </div>
        )}

        {parsedResults.note && (
          <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded-md">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              {parsedResults.note}
            </p>
          </div>
        )}
      </Card>

      {/* Step Results */}
      <div className="space-y-2">
        {parsedResults.steps.map((step, index) => {
          const isExpanded = expandedSteps.has(step.step_id);

          return (
            <Card key={step.step_id} className="overflow-hidden p-0 gap-0">
              {/* Step Header */}
              <button
                className="w-full px-3 py-2.5 flex items-center gap-2.5 hover:bg-muted/50 transition-colors text-left"
                onClick={() => toggleStep(step.step_id)}
              >
                <div className="flex-shrink-0">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>

                <div className="flex-shrink-0">
                  {(() => {
                    const ActionIcon =
                      ACTION_ICONS[step.action_type] || AlertCircle;
                    return (
                      <div
                        className={cn(
                          "w-6 h-6 rounded flex items-center justify-center",
                          step.status === "success" || step.success === true
                            ? "bg-muted text-foreground"
                            : "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400"
                        )}
                      >
                        <ActionIcon className="h-3.5 w-3.5" />
                      </div>
                    );
                  })()}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs font-mono text-muted-foreground">
                      #{step.step_number || index + 1}
                    </span>
                    <span className="font-medium truncate">
                      {ACTION_LABELS[step.action_type] || step.action_type}
                    </span>
                    {step.description && (
                      <span className="text-xs text-muted-foreground truncate">
                        {step.description}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {formatDuration(step.duration_ms)}
                  </div>
                </div>

                <div className="flex-shrink-0">
                  {step.status === "success" || step.success === true ? (
                    <CheckCircle2 className="h-4 w-4 text-success-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t bg-muted/20 space-y-3.5">
                  {/* Error Message */}
                  {step.error && (
                    <div className="p-3 bg-red-50 dark:bg-red-950 rounded-md border border-red-200 dark:border-red-800">
                      <p className="text-xs font-semibold text-red-800 dark:text-red-200 mb-1.5">
                        Error
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-300 font-mono">
                        {step.error}
                      </p>
                    </div>
                  )}

                  {/* Nested sub-steps (loop / if) */}
                  {step.sub_steps && step.sub_steps.length > 0 && (
                    <LoopIterationSteps
                      parentKey={step.step_id}
                      subSteps={step.sub_steps}
                      expandedSteps={expandedSteps}
                      onToggle={toggleStep}
                      onScreenshotClick={setSelectedScreenshot}
                    />
                  )}

                  {/* Extracted Data */}
                  {step.extracted_data && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800">
                      <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-2">
                        Extracted Data
                      </p>
                      <pre className="text-xs text-blue-700 dark:text-blue-300 font-mono overflow-auto max-h-[300px]">
                        {typeof step.extracted_data === "string"
                          ? step.extracted_data
                          : JSON.stringify(step.extracted_data, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Screenshot (hidden when agent sub-steps carry their own) */}
                  {step.screenshot &&
                    !(
                      step.agent_sub_steps && step.agent_sub_steps.length > 0
                    ) && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">Screenshot</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setSelectedScreenshot(step.screenshot!)
                            }
                            className="h-8"
                          >
                            <ZoomIn className="h-3.5 w-3.5" />
                            View Full Size
                          </Button>
                        </div>
                        <div
                          className="relative rounded-md overflow-hidden border cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                          onClick={() =>
                            setSelectedScreenshot(step.screenshot!)
                          }
                        >
                          <img
                            src={`data:image/png;base64,${step.screenshot}`}
                            alt={`Screenshot for ${step.step_id}`}
                            className="w-full h-auto"
                          />
                        </div>
                      </div>
                    )}

                  {/* Agent Sub-Steps Slideshow */}
                  {step.agent_sub_steps && step.agent_sub_steps.length > 0 && (
                    <AgentSubStepSlideshow subSteps={step.agent_sub_steps} />
                  )}

                  {/* Agent Chat Messages */}
                  {step.agent_chat_messages &&
                    step.agent_chat_messages.length > 0 && (
                      <AgentChatView messages={step.agent_chat_messages} />
                    )}

                  {/* Step Details */}
                  <div className="grid grid-cols-2 gap-3 text-xs pt-1">
                    <div>
                      <span className="text-muted-foreground">Action: </span>
                      <span className="font-medium">{step.action_type}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Duration: </span>
                      <span className="font-medium">
                        {formatDuration(step.duration_ms)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status: </span>
                      <span
                        className={cn(
                          "font-medium",
                          step.status === "success" && "text-success-600",
                          step.status === "failed" && "text-red-600"
                        )}
                      >
                        {step.status}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Screenshot Modal */}
      <Dialog
        open={selectedScreenshot !== null}
        onOpenChange={(open) => !open && setSelectedScreenshot(null)}
      >
        <DialogContent
          size="full"
          className="!w-[90%] !h-[90%] !max-w-[90%] overflow-auto p-6"
        >
          <DialogHeader>
            <DialogTitle>Screenshot</DialogTitle>
          </DialogHeader>
          {selectedScreenshot && (
            <img
              src={`data:image/png;base64,${selectedScreenshot}`}
              alt="Full size screenshot"
              className="w-full h-auto rounded-md"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Slideshow Modal */}
      <Dialog
        open={showSlideshow}
        onOpenChange={(open) => !open && handleCloseSlideshow()}
      >
        <DialogContent
          size="full"
          className="!w-[90%] !h-[90%] !max-w-[90%] overflow-hidden p-0"
        >
          <div className="flex flex-col h-full">
            {/* Slideshow Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <DialogTitle>Execution Slideshow</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {screenshots.length > 0 && (
                    <>
                      Step {screenshots[currentSlideIndex].step_number} of{" "}
                      {parsedResults.total_steps} •{" "}
                      <Badge variant="outline" className="text-xs">
                        {(() => {
                          const at = screenshots[currentSlideIndex].action_type;
                          const colonIdx = at.indexOf(":");
                          if (colonIdx >= 0) {
                            const prefix = at.slice(0, colonIdx + 1);
                            const base = at.slice(colonIdx + 1);
                            const baseLabel = ACTION_LABELS[base] || base;
                            return `${prefix} ${baseLabel}`;
                          }
                          return ACTION_LABELS[at] || at;
                        })()}
                      </Badge>
                    </>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-2 mr-8">
                {/* Speed Control */}
                <select
                  value={playbackSpeed}
                  onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                  className="text-sm border rounded px-2 py-1"
                >
                  <option value={500}>0.5s</option>
                  <option value={1000}>1s</option>
                  <option value={2000}>2s</option>
                  <option value={3000}>3s</option>
                </select>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePrevSlide}
                  disabled={screenshots.length === 0}
                >
                  <SkipBack className="h-4 w-4" />
                </Button>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={togglePlayPause}
                  disabled={screenshots.length === 0}
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNextSlide}
                  disabled={screenshots.length === 0}
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Slideshow Content */}
            <div className="flex-1 overflow-auto p-6 bg-muted/20">
              {screenshots.length > 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="relative max-w-full max-h-full">
                    <img
                      src={`data:image/png;base64,${screenshots[currentSlideIndex].screenshot}`}
                      alt={`Step ${screenshots[currentSlideIndex].step_number}`}
                      className="max-w-full max-h-[calc(90vh-180px)] object-contain rounded-lg shadow-lg"
                    />

                    {/* Step Info Overlay */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/75 text-white px-4 py-2 rounded-lg backdrop-blur-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono">
                          #{screenshots[currentSlideIndex].step_number}
                        </span>
                        <span className="text-sm">
                          {screenshots[currentSlideIndex].step_id}
                        </span>
                        <Badge
                          variant="secondary"
                          className={cn(
                            "text-xs",
                            screenshots[currentSlideIndex].status ===
                              "success" && "bg-success-500 text-white",
                            screenshots[currentSlideIndex].status ===
                              "failed" && "bg-red-500 text-white"
                          )}
                        >
                          {screenshots[currentSlideIndex].status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <p>No screenshots available</p>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {screenshots.length > 0 && (
              <div className="px-6 py-3 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {currentSlideIndex + 1} / {screenshots.length}
                  </span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{
                        width: `${((currentSlideIndex + 1) / screenshots.length) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
